import { latLonToMercatorPixel, webMercatorWorldSize } from "../data/goesGrid.js";

const PANE = "satellitePane";
const PANE_Z = "350";

const FRAGMENT = `
precision highp float;
uniform vec2 u_resolution;
uniform float u_dpr;
uniform vec2 u_mercOrigin;
uniform float u_worldSize;
uniform float u_req;
uniform float u_rpol;
uniform float u_H;
uniform float u_lon0;
uniform float u_xAngle0;
uniform float u_xAngleStep;
uniform float u_yAngle0;
uniform float u_yAngleStep;
uniform float u_x0;
uniform float u_y0;
uniform float u_stride;
uniform vec2 u_size;
uniform float u_opacity;
uniform sampler2D u_grid;

void main() {
  vec2 frag = vec2(gl_FragCoord.x, u_resolution.y - gl_FragCoord.y);
  float cssX = frag.x / u_dpr;
  float cssY = frag.y / u_dpr;
  float mercX = u_mercOrigin.x + cssX;
  float mercY = u_mercOrigin.y + cssY;
  float nx = mercX / u_worldSize;
  float ny = mercY / u_worldSize;
  float lonDeg = nx * 360.0 - 180.0;
  float merc = 3.141592653589793 * (1.0 - 2.0 * ny);
  float e = exp(merc);
  float lat = atan((e - 1.0 / e) * 0.5);

  float lon = radians(lonDeg);
  float lambda0 = radians(u_lon0);
  float phiC = atan((u_rpol * u_rpol) / (u_req * u_req) * tan(lat));
  float ecc = ((u_req * u_req) - (u_rpol * u_rpol)) / (u_req * u_req);
  float rc = u_rpol / sqrt(1.0 - ecc * cos(phiC) * cos(phiC));
  float sx = u_H - rc * cos(phiC) * cos(lon - lambda0);
  float sy = -rc * cos(phiC) * sin(lon - lambda0);
  float sz = rc * sin(phiC);
  if (sx <= 0.0) discard;
  if (u_H * (u_H - sx) < sy * sy + (u_req * u_req) / (u_rpol * u_rpol) * sz * sz) discard;
  float norm = sqrt(sx * sx + sy * sy + sz * sz);
  float scanX = asin(-sy / norm);
  float scanY = atan(sz / sx);
  float ix = (scanX - u_xAngle0) / u_xAngleStep - 0.5;
  float iy = (scanY - u_yAngle0) / u_yAngleStep - 0.5;
  // A decimated frame keeps every stride-th cell from x0/y0; each one covers
  // the stride-wide span centered on it.
  float col = floor((ix - u_x0) / u_stride + 0.5);
  float row = floor((iy - u_y0) / u_stride + 0.5);
  if (col < 0.0 || row < 0.0 || col >= u_size.x || row >= u_size.y) discard;
  vec2 uv = (vec2(col, row) + 0.5) / u_size;
  vec4 color = texture2D(u_grid, uv);
  if (color.a <= 0.0) discard;
  color.a *= u_opacity;
  gl_FragColor = color;
}
`;

const VERTEX = `
attribute vec2 a_pos;
void main() {
  gl_Position = vec4(a_pos, 0.0, 1.0);
}
`;

function compile(gl, type, source) {
  const shader = gl.createShader(type);
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const err = gl.getShaderInfoLog(shader);
    gl.deleteShader(shader);
    throw new Error(err || "Satellite shader failed");
  }
  return shader;
}

export function createSatelliteCanvasLayer(L) {
  return L.Layer.extend({
    initialize: function () {
      this._frame = null;
      this._opacity = 0.8;
      this._raf = null;
      this._texGeneration = 0;
      this._uploadedGeneration = -1;
      this._canvas = document.createElement("canvas");
      this._canvas.className = "leaflet-zoom-animated goes-canvas";
      this._canvas.style.position = "absolute";
      this._canvas.style.pointerEvents = "none";
      this._canvas.style.opacity = "1";
      this._gl = this._canvas.getContext("webgl", {
        alpha: true,
        antialias: false,
        depth: false,
        stencil: false,
        premultipliedAlpha: false,
        preserveDrawingBuffer: false,
      });
      if (!this._gl) {
        console.error("Satellite layer needs WebGL.");
        return;
      }
      try {
        this._initGl();
      } catch (err) {
        console.error("Satellite layer failed to start.", err);
        this._gl = null;
      }
    },

    _initGl: function () {
      const gl = this._gl;
      const vs = compile(gl, gl.VERTEX_SHADER, VERTEX);
      const fs = compile(gl, gl.FRAGMENT_SHADER, FRAGMENT);
      const program = gl.createProgram();
      gl.attachShader(program, vs);
      gl.attachShader(program, fs);
      gl.linkProgram(program);
      gl.deleteShader(vs);
      gl.deleteShader(fs);
      if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
        const err = gl.getProgramInfoLog(program);
        gl.deleteProgram(program);
        throw new Error(err || "Satellite program failed");
      }
      this._program = program;
      gl.useProgram(program);

      const quad = new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]);
      this._quad = gl.createBuffer();
      gl.bindBuffer(gl.ARRAY_BUFFER, this._quad);
      gl.bufferData(gl.ARRAY_BUFFER, quad, gl.STATIC_DRAW);
      const aPos = gl.getAttribLocation(program, "a_pos");
      gl.enableVertexAttribArray(aPos);
      gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);

      const loc = (name) => gl.getUniformLocation(program, name);
      this._uResolution = loc("u_resolution");
      this._uDpr = loc("u_dpr");
      this._uMercOrigin = loc("u_mercOrigin");
      this._uWorldSize = loc("u_worldSize");
      this._uReq = loc("u_req");
      this._uRpol = loc("u_rpol");
      this._uH = loc("u_H");
      this._uLon0 = loc("u_lon0");
      this._uXAngle0 = loc("u_xAngle0");
      this._uXAngleStep = loc("u_xAngleStep");
      this._uYAngle0 = loc("u_yAngle0");
      this._uYAngleStep = loc("u_yAngleStep");
      this._uX0 = loc("u_x0");
      this._uY0 = loc("u_y0");
      this._uStride = loc("u_stride");
      this._uSize = loc("u_size");
      this._uOpacity = loc("u_opacity");

      this._tex = gl.createTexture();
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, this._tex);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.uniform1i(loc("u_grid"), 0);
    },

    onAdd: function (map) {
      this._map = map;
      if (!map.getPane(PANE)) {
        const pane = map.createPane(PANE);
        pane.style.zIndex = PANE_Z;
        pane.style.pointerEvents = "none";
      }
      map.getPane(PANE).appendChild(this._canvas);
      map.on("move zoom resize viewreset", this._scheduleRender, this);
      this._scheduleRender();
      return this;
    },

    onRemove: function (map) {
      if (this._raf) {
        cancelAnimationFrame(this._raf);
        this._raf = null;
      }
      map.off("move zoom resize viewreset", this._scheduleRender, this);
      if (this._canvas.parentNode) this._canvas.parentNode.removeChild(this._canvas);
      this._map = null;
      return this;
    },

    setFrame: function (frame) {
      this._frame = frame || null;
      this._texGeneration += 1;
      this._scheduleRender();
      return this;
    },

    // The largest crop the GPU can take as one texture.
    maxTextureSize: function () {
      return this._gl ? this._gl.getParameter(this._gl.MAX_TEXTURE_SIZE) : 0;
    },

    setOpacity: function (opacity) {
      const value = Number(opacity);
      this._opacity = Number.isFinite(value) ? Math.max(0, Math.min(1, value)) : 0.8;
      this._scheduleRender();
      return this;
    },

    _uploadTexture: function () {
      if (this._uploadedGeneration === this._texGeneration) return;
      const gl = this._gl;
      const frame = this._frame;
      this._uploadedGeneration = this._texGeneration;
      if (!frame?.rgba || !(frame.width > 0) || !(frame.height > 0)) return;
      gl.bindTexture(gl.TEXTURE_2D, this._tex);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(
        gl.TEXTURE_2D,
        0,
        gl.RGBA,
        frame.width,
        frame.height,
        0,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        frame.rgba,
      );
    },

    _scheduleRender: function () {
      if (!this._map) return;
      const topLeft = this._map.containerPointToLayerPoint([0, 0]);
      L.DomUtil.setPosition(this._canvas, topLeft);
      const mapSize = this._map.getSize();
      const dpr = window.devicePixelRatio || 1;
      const w = Math.max(1, Math.round(mapSize.x * dpr));
      const h = Math.max(1, Math.round(mapSize.y * dpr));
      if (this._canvas.width !== w || this._canvas.height !== h) {
        this._canvas.width = w;
        this._canvas.height = h;
        this._canvas.style.width = `${mapSize.x}px`;
        this._canvas.style.height = `${mapSize.y}px`;
      }
      if (this._raf) return;
      this._raf = requestAnimationFrame(() => {
        this._raf = null;
        this._render();
      });
    },

    _render: function () {
      if (!this._map || !this._gl) return;
      const gl = this._gl;
      const dpr = window.devicePixelRatio || 1;
      const mapSize = this._map.getSize();
      const width = Math.max(1, Math.round(mapSize.x * dpr));
      const height = Math.max(1, Math.round(mapSize.y * dpr));
      gl.viewport(0, 0, width, height);
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      const frame = this._frame;
      const proj = frame?.proj;
      if (!frame?.rgba || !proj) return;

      this._uploadTexture();
      gl.useProgram(this._program);
      const nw = this._map.containerPointToLatLng([0, 0]);
      const world = webMercatorWorldSize(this._map.getZoom());
      const origin = latLonToMercatorPixel(nw.lat, nw.lng, world);
      gl.uniform2f(this._uResolution, width, height);
      gl.uniform1f(this._uDpr, dpr);
      gl.uniform2f(this._uMercOrigin, origin.x, origin.y);
      gl.uniform1f(this._uWorldSize, world);
      gl.uniform1f(this._uReq, proj.req);
      gl.uniform1f(this._uRpol, proj.rpol);
      gl.uniform1f(this._uH, proj.height + proj.req);
      gl.uniform1f(this._uLon0, proj.lon0);
      gl.uniform1f(this._uXAngle0, proj.xAngle0);
      gl.uniform1f(this._uXAngleStep, proj.xAngleStep);
      gl.uniform1f(this._uYAngle0, proj.yAngle0);
      gl.uniform1f(this._uYAngleStep, proj.yAngleStep);
      gl.uniform1f(this._uX0, frame.x0);
      gl.uniform1f(this._uY0, frame.y0);
      gl.uniform1f(this._uStride, frame.stride || 1);
      gl.uniform2f(this._uSize, frame.width, frame.height);
      gl.uniform1f(this._uOpacity, this._opacity);
      gl.bindBuffer(gl.ARRAY_BUFFER, this._quad);
      gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4);
    },
  });
}
